import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';

import { catalogApi, companyApi } from '@/api';
import { resolveFileUrl } from '@/api/client';
import {
  Alert,
  Button,
  Card,
  CardHeader,
  ErrorState,
  FileUploader,
  Icon,
  Input,
  LinkButton,
  LoadingState,
  PageHeader,
  ProgressBar,
  Select,
  Textarea,
  useToast,
} from '@/components/ui';
import {
  DESCRIPTION_MINIMUM,
  GUIDE_STEPS,
  NEEDS_VERIFICATION,
  SUBMIT_STEP,
  completedKeys,
  firstOpenStep,
  missingFields,
  nextStepAfter,
} from '@/features/verification/guideSteps';
import { applyFieldErrors, errorMessage } from '@/hooks/useApiError';
import { paths } from '@/routes/paths';
import { cn } from '@/utils/cn';
import { companyProfileSchema, companyVerificationSchema } from '@/validations';

const COMPANY_SIZES = ['1-10', '11-50', '51-200', '201-500', '501-1000', '1000+'];
const MAX_LOGO_BYTES = 2 * 1024 * 1024;

function formValues(company) {
  return {
    industry_id: company.industry?.id ?? '',
    company_size: company.company_size ?? '',
    description: company.description ?? '',
    founded_year: company.founded_year ?? '',
    location: company.location ?? '',
    contact_email: company.contact_email ?? '',
    contact_phone: company.contact_phone ?? '',
    website: company.website ?? '',
    linkedin_url: company.linkedin_url ?? '',
  };
}

/**
 * "Get verified": the company profile and the verification request as one
 * step-by-step path. Each step saves on its own and the next open one follows,
 * so an unverified company is never left wondering what is still missing.
 */
export function CompanyVerificationGuidePage() {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [current, setCurrent] = useState(null);

  const profile = useQuery({ queryKey: ['company-profile'], queryFn: companyApi.getProfile });
  const completion = useQuery({
    queryKey: ['company-completion'],
    queryFn: companyApi.getCompletion,
  });
  const verification = useQuery({
    queryKey: ['company-verification'],
    queryFn: companyApi.getVerification,
  });
  const { data: industries } = useQuery({
    queryKey: ['industries'],
    queryFn: () => catalogApi.listIndustries(),
  });

  const form = useForm({ defaultValues: formValues({}) });
  const { reset } = form;
  const company = profile.data;

  // Filled once, from the saved profile. Later saves keep what was typed.
  const [filled, setFilled] = useState(false);
  useEffect(() => {
    if (!company || filled) return;
    reset(formValues(company));
    setFilled(true);
  }, [company, filled, reset]);

  const done = completedKeys(completion.data);

  /**
   * After a save, read completion again and go to whatever is still open.
   *
   * `staleTime: 0` is the point: the app-wide default would hand back the
   * completion cached a few seconds ago, without the step just saved -- no
   * tick, and the guide treating that step as still open. If the read fails,
   * the step counts as done anyway (it passed the same checks the server
   * makes), so the guide never stalls on a save that worked.
   */
  async function advanceFrom(stepKey) {
    let finished;
    try {
      const fresh = await queryClient.fetchQuery({
        queryKey: ['company-completion'],
        queryFn: companyApi.getCompletion,
        staleTime: 0,
      });
      finished = completedKeys(fresh);
    } catch {
      finished = new Set([...done, stepKey]);
      void queryClient.invalidateQueries({ queryKey: ['company-completion'] });
    }
    // Whether the request can be sent now; the submit step reads it.
    void queryClient.invalidateQueries({ queryKey: ['company-verification'] });
    setCurrent(nextStepAfter(stepKey, finished));
  }

  if (profile.isPending || completion.isPending || verification.isPending) {
    return <LoadingState label="Loading your verification steps…" />;
  }
  const failed = [profile, completion, verification].find((query) => query.isError);
  if (failed) {
    return (
      <ErrorState message={errorMessage(failed.error)} onRetry={() => void failed.refetch()} />
    );
  }

  const status = verification.data.verification_status;
  if (!NEEDS_VERIFICATION.has(status)) {
    return <Finished status={status} />;
  }

  const active = current ?? firstOpenStep(done);
  const step = GUIDE_STEPS.find((item) => item.key === active);
  const position = GUIDE_STEPS.findIndex((item) => item.key === active) + 1;

  return (
    <div>
      <PageHeader
        eyebrow="Organisation"
        title="Get your company verified"
        description="Until your company is verified, you cannot post internships. Work through these steps — each one saves on its own and the next one opens."
      />

      {status === 'REJECTED' && (
        <Alert tone="error" className="mb-6" title="Your last request was not approved">
          {verification.data.rejection_reason ? (
            <p className="break-words whitespace-pre-line">
              <span className="font-medium">Reason:</span> {verification.data.rejection_reason}
            </p>
          ) : null}
          <p className={verification.data.rejection_reason ? 'mt-1' : undefined}>
            Fix those details below and submit again.
          </p>
        </Alert>
      )}

      <div className="grid items-start gap-6 lg:grid-cols-[280px_1fr]">
        <Card>
          <ProgressBar
            value={completion.data?.percentage ?? company.profile_completion ?? 0}
            label="Profile complete"
          />
          <ol className="mt-5 space-y-1" aria-label="Verification steps">
            {GUIDE_STEPS.map((item, index) => {
              const isDone = item.key !== SUBMIT_STEP && done.has(item.key);
              const isActive = item.key === active;
              return (
                <li key={item.key}>
                  <button
                    type="button"
                    onClick={() => setCurrent(item.key)}
                    aria-current={isActive ? 'step' : undefined}
                    className={cn(
                      'flex w-full items-center gap-3 rounded-md px-2 py-2 text-left text-sm transition',
                      isActive
                        ? 'bg-brand-50 text-brand-700 font-medium'
                        : 'hover:bg-surface-muted',
                    )}
                  >
                    {isDone ? (
                      <span
                        aria-hidden="true"
                        className="bg-success-600 grid size-6 shrink-0 place-items-center rounded-full text-white"
                      >
                        <Icon name="check" size={14} />
                      </span>
                    ) : (
                      <span
                        aria-hidden="true"
                        className={cn(
                          'grid size-6 shrink-0 place-items-center rounded-full border-2 text-xs font-semibold',
                          isActive
                            ? 'border-brand-600 text-brand-700'
                            : 'border-line text-ink-muted',
                        )}
                      >
                        {index + 1}
                      </span>
                    )}
                    <span className={cn(!isActive && (isDone ? 'text-ink-muted' : 'text-ink'))}>
                      {item.title}
                      <span className="sr-only">{isDone ? ' (done)' : ' (to do)'}</span>
                    </span>
                  </button>
                </li>
              );
            })}
          </ol>
        </Card>

        <Card>
          <CardHeader
            title={`Step ${position} of ${GUIDE_STEPS.length}: ${step.title}`}
            description={step.description}
          />
          {active === SUBMIT_STEP ? (
            <SubmitStep
              verification={verification.data}
              onJump={setCurrent}
              onSubmitted={() => {
                void queryClient.invalidateQueries({ queryKey: ['company-verification'] });
                void queryClient.invalidateQueries({ queryKey: ['company-profile'] });
              }}
            />
          ) : active === 'logo' ? (
            <LogoStep
              company={company}
              isDone={done.has('logo')}
              onUploaded={(updated) => {
                queryClient.setQueryData(['company-profile'], updated);
                toast.success('Logo saved');
                void advanceFrom('logo');
              }}
              onSkip={() => setCurrent(nextStepAfter('logo', done))}
            />
          ) : (
            <ProfileStep
              key={active}
              stepKey={active}
              form={form}
              industries={industries ?? []}
              onSaved={(updated) => {
                queryClient.setQueryData(['company-profile'], updated);
                toast.success(`${step.title} saved`);
                void advanceFrom(active);
              }}
              onSkip={() => setCurrent(nextStepAfter(active, done))}
            />
          )}
        </Card>
      </div>
    </div>
  );
}

function StepActions({ isSaving, onSkip, saveLabel = 'Save and continue' }) {
  return (
    <div className="border-line mt-6 flex flex-wrap items-center justify-end gap-2 border-t pt-4">
      {onSkip && (
        <Button type="button" variant="ghost" onClick={onSkip}>
          Skip for now
        </Button>
      )}
      <Button type="submit" isLoading={isSaving}>
        {saveLabel}
        <Icon name="arrowRight" size={15} />
      </Button>
    </div>
  );
}

function ProfileStep({ stepKey, form, industries, onSaved, onSkip }) {
  const toast = useToast();
  const step = GUIDE_STEPS.find((item) => item.key === stepKey);
  const {
    register,
    handleSubmit,
    setError,
    clearErrors,
    watch,
    formState: { errors },
  } = form;

  const save = useMutation({ mutationFn: (values) => companyApi.updateProfile(values) });

  async function onSubmit(values) {
    clearErrors();
    const missing = missingFields(stepKey, values);
    if (missing.length > 0) {
      missing.forEach(({ field, message }) => setError(field, { message }));
      return;
    }
    // Only this step's fields, checked with the same rules as the profile form.
    const picked = Object.fromEntries(step.fields.map((field) => [field, values[field]]));
    const parsed = companyProfileSchema
      .pick(Object.fromEntries(step.fields.map((field) => [field, true])))
      .safeParse(picked);
    if (!parsed.success) {
      parsed.error.issues.forEach((issue) => setError(issue.path[0], { message: issue.message }));
      return;
    }
    const payload = Object.fromEntries(
      step.fields.map((field) => [field, parsed.data[field] ?? null]),
    );
    try {
      onSaved(await save.mutateAsync(payload));
    } catch (mutationError) {
      if (!applyFieldErrors(mutationError, setError)) {
        toast.error(errorMessage(mutationError, 'Could not save this step'));
      }
    }
  }

  const description = watch('description') ?? '';

  return (
    <form onSubmit={handleSubmit(onSubmit)} noValidate>
      {stepKey === 'basics' && (
        <div className="grid gap-4 sm:grid-cols-2">
          <Select
            label="Industry"
            required
            error={errors.industry_id?.message}
            {...register('industry_id')}
          >
            <option value="">Select an industry</option>
            {industries.map((industry) => (
              <option key={industry.id} value={industry.id}>
                {industry.name}
              </option>
            ))}
          </Select>
          <Select
            label="Company size"
            required
            error={errors.company_size?.message}
            {...register('company_size')}
          >
            <option value="">Select a size</option>
            {COMPANY_SIZES.map((size) => (
              <option key={size} value={size}>
                {size} employees
              </option>
            ))}
          </Select>
        </div>
      )}

      {stepKey === 'description' && (
        <Textarea
          label="About your company"
          required
          rows={7}
          placeholder="What your company does, who your customers are, and what interns will work on."
          hint={`${description.trim().length} / ${DESCRIPTION_MINIMUM} characters minimum`}
          error={errors.description?.message}
          {...register('description')}
        />
      )}

      {stepKey === 'contact' && (
        <div className="grid gap-4 sm:grid-cols-2">
          <Input
            label="Contact email"
            type="email"
            required
            autoComplete="email"
            error={errors.contact_email?.message}
            {...register('contact_email')}
          />
          <Input
            label="Contact phone"
            type="tel"
            required
            autoComplete="tel"
            error={errors.contact_phone?.message}
            {...register('contact_phone')}
          />
        </div>
      )}

      {stepKey === 'details' && (
        <div className="grid gap-4 sm:grid-cols-2">
          <Input
            label="Location"
            required
            placeholder="Kolkata, India"
            error={errors.location?.message}
            {...register('location')}
          />
          <Input
            label="Founded"
            type="number"
            required
            placeholder="2015"
            error={errors.founded_year?.message}
            {...register('founded_year')}
          />
        </div>
      )}

      {stepKey === 'links' && (
        <div className="grid gap-4 sm:grid-cols-2">
          <Input
            label="Website"
            placeholder="https://example.com"
            hint="Either one is enough."
            error={errors.website?.message}
            {...register('website')}
          />
          <Input
            label="LinkedIn"
            placeholder="https://linkedin.com/company/example"
            error={errors.linkedin_url?.message}
            {...register('linkedin_url')}
          />
        </div>
      )}

      <StepActions isSaving={save.isPending} onSkip={onSkip} />
    </form>
  );
}

function LogoStep({ company, isDone, onUploaded, onSkip }) {
  const toast = useToast();
  const upload = useMutation({
    mutationFn: (file) => companyApi.uploadLogo(file),
    onSuccess: onUploaded,
    onError: (uploadError) => toast.error(errorMessage(uploadError, 'Could not upload the logo')),
  });

  return (
    <div>
      {company.logo_url && (
        <img
          src={resolveFileUrl(company.logo_url)}
          alt={`${company.name} logo`}
          className="border-line mb-4 h-20 w-20 rounded-lg border object-contain"
        />
      )}
      <FileUploader
        accept="image/jpeg,image/png,image/webp"
        maxSizeBytes={MAX_LOGO_BYTES}
        isUploading={upload.isPending}
        hint="JPEG, PNG or WebP, up to 2 MB. The next step opens once it is uploaded."
        onSelect={(file) => upload.mutate(file)}
      />
      <div className="border-line mt-6 flex justify-end gap-2 border-t pt-4">
        <Button type="button" variant={isDone ? 'primary' : 'ghost'} onClick={onSkip}>
          {isDone ? 'Continue' : 'Skip for now'}
          {isDone && <Icon name="arrowRight" size={15} />}
        </Button>
      </div>
    </div>
  );
}

const SECTION_TITLES = Object.fromEntries(GUIDE_STEPS.map((step) => [step.key, step.title]));

function SubmitStep({ verification, onJump, onSubmitted }) {
  const toast = useToast();
  const {
    register,
    handleSubmit,
    setError,
    formState: { errors },
  } = useForm({
    // What the last request said, so a resubmission after a rejection starts
    // from it rather than from nothing.
    defaultValues: {
      registration_number: verification.registration_number ?? '',
      note: verification.note ?? '',
    },
  });
  const submit = useMutation({
    mutationFn: (values) => companyApi.submitVerification(values),
  });

  if (!verification.can_submit) {
    return (
      <div className="space-y-4">
        <Alert tone="warning" title="A few more steps first">
          Your profile needs to be at least 60% complete before you can ask for verification. Still
          open:
        </Alert>
        <div className="flex flex-wrap gap-2">
          {verification.blocking_sections.map((key) => (
            <Button
              key={key}
              type="button"
              variant="secondary"
              size="sm"
              onClick={() => onJump(key)}
            >
              {SECTION_TITLES[key] ?? key}
              <Icon name="arrowRight" size={14} />
            </Button>
          ))}
        </div>
      </div>
    );
  }

  async function onSubmit(values) {
    const parsed = companyVerificationSchema.safeParse(values);
    if (!parsed.success) {
      parsed.error.issues.forEach((issue) => setError(issue.path[0], { message: issue.message }));
      return;
    }
    const payload = Object.fromEntries(
      Object.entries(parsed.data).filter(([, value]) => value !== '' && value !== undefined),
    );
    try {
      await submit.mutateAsync(payload);
      toast.success('Verification request submitted');
      onSubmitted();
    } catch (mutationError) {
      if (!applyFieldErrors(mutationError, setError)) {
        toast.error(errorMessage(mutationError, 'Could not submit the request'));
      }
    }
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} noValidate>
      <div className="space-y-4">
        <Input
          label="Company registration number"
          hint="CIN, GSTIN or an equivalent registration. It speeds up the review."
          error={errors.registration_number?.message}
          {...register('registration_number')}
        />
        <Textarea
          label="Anything we should know?"
          rows={4}
          error={errors.note?.message}
          {...register('note')}
        />
      </div>
      <StepActions isSaving={submit.isPending} saveLabel="Submit for review" />
    </form>
  );
}

/** Nothing left to guide: already asked, or already verified. */
function Finished({ status }) {
  const pending = status === 'PENDING';
  return (
    <div className="mx-auto max-w-xl py-10 text-center">
      <span
        aria-hidden="true"
        className={cn(
          'mx-auto mb-4 grid size-14 place-items-center rounded-2xl ring-1 ring-inset',
          pending
            ? 'bg-warning-50 text-warning-800 ring-warning-100'
            : 'bg-success-50 text-success-700 ring-success-100',
        )}
      >
        <Icon name={pending ? 'clock' : 'shieldCheck'} size={26} />
      </span>
      <h1 className="text-ink text-xl font-semibold tracking-tight">
        {pending ? 'Your request is with our team' : 'Your company is verified'}
      </h1>
      <p className="text-ink-muted mt-2 text-sm">
        {pending
          ? 'Reviews usually take one or two working days. We will notify you as soon as there is a decision. Once you are verified you can post internships; each one goes live after Join-Wonder approves it.'
          : 'You can now post internships. Each one goes live, with the verified badge, once Join-Wonder approves it.'}
      </p>
      <div className="mt-6 flex justify-center gap-2">
        <LinkButton to={paths.company.dashboard} variant="secondary">
          Go to overview
        </LinkButton>
        <LinkButton to={paths.company.internships}>
          {pending ? 'Draft an internship' : 'Your internships'}
        </LinkButton>
      </div>
    </div>
  );
}
